import React, { memo } from "react";

import { classNames, transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";

import { DRIVER_DOCUMENT_WARNING_DAYS, DRIVER_INTRO_SAMPLE } from "../consts";
import { DriverDocumentSceneProps } from "../types";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Korak isprave (vozačka, osobna, putovnica): kartica isprave, rok do isteka i podsjetnik. */
const DriverDocumentScene: React.FC<DriverDocumentSceneProps> = ({ formValues, step }) => {
  const number = (formValues[step.numberField] as string | null) || DRIVER_INTRO_SAMPLE.documentNumber;
  const savedExpiry = formValues[step.expiryField] as Date | null;
  const expiry = savedExpiry ? new Date(savedExpiry) : new Date(Date.now() + DRIVER_INTRO_SAMPLE.expiryInDays * DAY_MS);
  const daysLeft = Math.ceil((expiry.getTime() - Date.now()) / DAY_MS);
  const isExpired = daysLeft < 0;
  const isExpiringSoon = !isExpired && daysLeft <= DRIVER_DOCUMENT_WARNING_DAYS;
  const uploads = formValues[step.uploadsField];
  const preview = uploads.find((upload) => upload.contentType?.startsWith("image/"))?.url;

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          {/* Isprava kao kartica: preslika ako je priložena, inače obris isprave. */}
          <div className="relative h-[8.5rem] rounded-md bg-gradient-to-br from-sky-50 to-blue-100 overflow-hidden">
            {preview ? (
              <img src={preview} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="flex h-full gap-3 p-3">
                <span className="w-16 rounded-sm bg-white/80" />
                <div className="flex-1 space-y-2 pt-1">
                  <span className="block h-2 w-3/4 rounded-full bg-white/80" />
                  <span className="block h-2 w-1/2 rounded-full bg-white/80" />
                  <span className="block h-2 w-2/3 rounded-full bg-white/80" />
                  <span className="block h-2 w-1/3 rounded-full bg-white/80" />
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">{step.title}</div>
          <div className="text-sm font-bold text-slate-800 truncate">{number}</div>
          <div className="mt-1 text-[0.65rem] text-slate-400">
            Vrijedi do {transformDateToLocalDateString(expiry)}
            {uploads.length > 0 && ` · ${uploads.length} ${uploads.length === 1 ? "preslika" : "preslike"}`}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Rok važenja</div>
          <div
            className={classNames(
              "mt-2 text-xl font-bold",
              isExpired ? "text-rose-600" : isExpiringSoon ? "text-amber-600" : "text-slate-800"
            )}
          >
            {isExpired ? "Istekla" : `${daysLeft} dana`}
          </div>
          <span
            className={classNames(
              "mt-2 inline-flex px-2 py-0.5 rounded-full text-[0.65rem] font-semibold",
              isExpired
                ? "bg-rose-100 text-rose-700"
                : isExpiringSoon
                  ? "bg-amber-100 text-amber-700"
                  : "bg-emerald-100 text-emerald-700"
            )}
          >
            {isExpired ? "Obnovite ispravu" : isExpiringSoon ? "Ističe uskoro" : "Važeća"}
          </span>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Podsjetnik prije isteka"
          subtitle={`${DRIVER_DOCUMENT_WARNING_DAYS} dana prije roka`}
          iconClassName="bg-amber-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(DriverDocumentScene);
